import {ariaLabelDate} from '@github-ui/aria-label-date'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useAnalytics} from '@github-ui/use-analytics'
import {ChevronDownIcon, ChevronRightIcon, StopIcon} from '@primer/octicons-react'
import {Button, Flash, IconButton, Label, Spinner} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense, useMemo, useRef, useState} from 'react'
import {useResolveThreadMutation} from '@github-ui/pull-requests/use-resolve-thread-mutation'
import {useUnresolveThreadMutation} from '@github-ui/pull-requests/use-unresolve-thread-mutation'
import {
  useComparisonInfo,
  usePullRequestBasePath,
  useSelectedCommentId,
} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useReviewThreadCollapseState} from '@github-ui/use-review-thread-collapse-state'
import {extractCommentAnchor} from '../../helpers/extract-comment-anchor'
import {
  buildUnifiedBatchItemFromThread,
  getFirstSubmittedComment,
} from '@github-ui/pull-requests/helpers/unified-batch-items'
import {useActiveRoute} from '@github-ui/pull-requests/hooks/use-active-route'

import {CommentErrorFallback} from './CommentErrorFallback'
import {
  AutomatedCommentSource,
  CommentReviewVariantType,
  isCopilotCodeReviewThread,
  type CommentAuthor,
  type ResolutionReasonType,
  type Thread,
} from '../../conversation-types'
import styles from './FileReviewThread.module.css'
import {ReviewThread, type ReviewThreadProps} from './ReviewThread'
import {fileLevelCommentKey} from '../../types'
import {isLoggedIn} from '@github-ui/client-env'
import {InlineAutomatedReviewThreadActions} from './InlineAutomatedReviewThreadActions'
import {ResolveThreadSplitButton} from './ResolveThreadSplitButton'
import {UnifiedCommentActions} from './UnifiedCommentActions'
import {
  UnifiedCommentActionTelemetryPlacements,
  useUnifiedCommentActionTelemetry,
} from './unified-comment-action-telemetry'

export interface FileReviewThreadProps extends Pick<ReviewThreadProps, 'viewerData'> {
  fileAnchor?: string
  filePath: string
  thread: Thread
  ghostUser?: CommentAuthor
  manuallyUpdateCommentsWithThisThreadId?: string
  isFirstThread?: boolean
}

export function FileReviewThread({
  fileAnchor,
  filePath,
  thread,
  isFirstThread,
  ghostUser,
  manuallyUpdateCommentsWithThisThreadId,
  ...rest
}: FileReviewThreadProps) {
  const reviewThreadRef = useRef<HTMLDivElement>(null)
  const isResolved = thread?.isResolved
  const [errorMessage, setErrorMessage] = useState('')
  const [isCollapsed, setIsCollapsed] = useReviewThreadCollapseState(thread.id, isResolved ?? false)

  const selectedCommentId = useSelectedCommentId()
  const {selectedRange} = useComparisonInfo()
  const basePath = usePullRequestBasePath()
  const surface = useActiveRoute()

  // Expand thread temporarily (without persisting) when navigating from the side panel
  const shouldForceExpand = useMemo(() => {
    if (selectedCommentId == null) return false
    return thread?.commentsData?.comments.some(comment => comment.databaseId === selectedCommentId) ?? false
  }, [selectedCommentId, thread])

  const effectiveIsCollapsed = isCollapsed && !shouldForceExpand

  const handleToggleCollapsed = () => {
    // Toggle based on the visible (effective) state so a click while force-expanded
    // collapses, rather than flipping the stored value the user can't see.
    setIsCollapsed(!effectiveIsCollapsed)
  }

  const {sendAnalyticsEvent} = useAnalytics()
  const threadResolutionReasonEnabled = isFeatureEnabled('thread_resolution_reason')
  const showResolutionReasons = threadResolutionReasonEnabled && isCopilotCodeReviewThread(thread)
  const unifiedBatchPrCommentsEnabled = isFeatureEnabled('unified_batch_pr_comments')

  const {mutate: resolveThreadMutation} = useResolveThreadMutation(filePath, fileLevelCommentKey)
  const handleResolveThread = (resolutionReason?: ResolutionReasonType) => {
    if (!thread || thread.isResolved) return

    resolveThreadMutation(
      {threadId: thread.id, resolutionReason},
      {
        onError: () => setErrorMessage('Failed to resolve thread'),
        onSuccess: () => {
          setIsCollapsed(true)
        },
      },
    )

    sendAnalyticsEvent('comments.resolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  const {mutate: unresolveThreadMutation} = useUnresolveThreadMutation(filePath, fileLevelCommentKey)
  const handleUnresolveThread = () => {
    if (!thread) return

    unresolveThreadMutation(
      {threadId: thread.id},
      {
        onError: () => setErrorMessage('Failed to unresolve thread'),
        onSuccess: () => {
          setIsCollapsed(false)
        },
      },
    )

    sendAnalyticsEvent('comments.unresolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  const hasComments = !!thread?.commentsData?.comments && thread.commentsData.comments.length > 0
  const unifiedBatchItem =
    unifiedBatchPrCommentsEnabled && thread && hasComments
      ? buildUnifiedBatchItemFromThread({filePath, thread})
      : undefined
  const sendUnifiedCommentActionTelemetry = useUnifiedCommentActionTelemetry(unifiedBatchItem, {
    controlPlacement: UnifiedCommentActionTelemetryPlacements.unifiedResolveRow,
  })

  if (!thread) return null

  if (!hasComments) return null

  const isAutomatedReviewThread =
    thread?.commentsData?.comments[0]?.reviewVariantType === CommentReviewVariantType.Automated
  const automatedComment = isAutomatedReviewThread ? thread.commentsData?.comments[0]?.automatedComment : null
  const dismissableAutomatedComment = automatedComment && automatedComment.dismissable
  const licenseComplianceAutomatedComment =
    automatedComment && automatedComment.source === AutomatedCommentSource.LicenseCompliance
  const firstSubmittedComment = getFirstSubmittedComment(thread)
  const batchToggleItem =
    unifiedBatchItem?.kind === 'ccr-without-suggestion' || unifiedBatchItem?.kind === 'human-without-suggestion'
      ? unifiedBatchItem
      : undefined
  const showBatchToggle = batchToggleItem !== undefined
  const commentAnchor = firstSubmittedComment ? extractCommentAnchor(firstSubmittedComment.url) : 'unknown'
  const fixWithCopilotDialogProps =
    unifiedBatchItem && firstSubmittedComment
      ? {
          basePath,
          commentAnchor,
          commentMessage: firstSubmittedComment.automatedComment?.message ?? firstSubmittedComment.body,
          commentPermalink: firstSubmittedComment.url,
          isOverviewComment: false,
          suggestion: firstSubmittedComment.automatedComment?.suggestion,
          suggestionState: firstSubmittedComment.automatedComment?.suggestionState,
          surface,
        }
      : undefined

  const isThreadResolved = !!thread.isResolved
  const userCanResolve = isLoggedIn()
  const isResolveable = userCanResolve && !!firstSubmittedComment

  const createdAt = thread.commentsData?.comments[0]?.createdAt
  const commentAuthorLogin = thread.commentsData?.comments[0]?.author?.login ?? 'Unknown Author'

  const buildResolutionLabel = (action: 'Resolve' | 'Unresolve') =>
    createdAt ? `${action} ${commentAuthorLogin}'s comment, ${ariaLabelDate(new Date(createdAt))}` : `${action} comment`
  const resolveButtonLabel = buildResolutionLabel('Resolve')
  const unresolveButtonLabel = buildResolutionLabel('Unresolve')

  return (
    <ErrorBoundary boundaryName="FileReviewThread" fallback={<CommentErrorFallback />}>
      <div className="rounded-2 bgColor-default" ref={reviewThreadRef}>
        <div className={clsx('px-1', styles.ReviewThreadContainer)}>
          <IconButton
            aria-label={effectiveIsCollapsed ? 'Expand comment' : 'Collapse comment'}
            icon={effectiveIsCollapsed ? ChevronRightIcon : ChevronDownIcon}
            size="small"
            variant="invisible"
            onClick={handleToggleCollapsed}
            data-is-first-collapse-button={isFirstThread}
          />
          <h2 className={styles.ReviewThreadHeading}>Comment on file</h2>

          <div className={styles.ReviewThreadInnerContainer}>
            {thread.positioning?.isOutdated && <Label variant="secondary">Outdated</Label>}

            {isThreadResolved && <Label variant="secondary">Resolved</Label>}
          </div>
        </div>
        <Suspense
          fallback={
            <div className={styles.SpinnerWrapper}>
              <Spinner />
              <p>Loading comments</p>
            </div>
          }
        >
          {errorMessage && (
            <Flash variant="danger" className="m-2">
              <StopIcon className="mr-2" />
              {errorMessage}
            </Flash>
          )}
          {!effectiveIsCollapsed && (
            <>
              <ReviewThread
                lineMarkersKey={fileLevelCommentKey}
                batchingEnabled
                filePath={filePath}
                gridCellId={`${filePath}-file-level-markers`}
                isInlineComment={false}
                thread={thread}
                shouldLimitHeight={false}
                selectedRange={selectedRange}
                {...rest}
                ghostUser={ghostUser}
              />
              {isResolveable &&
                !dismissableAutomatedComment &&
                !licenseComplianceAutomatedComment &&
                (unifiedBatchItem && fixWithCopilotDialogProps ? (
                  <UnifiedCommentActions
                    batchItem={batchToggleItem}
                    fixWithCopilotDialogProps={fixWithCopilotDialogProps}
                    isThreadResolved={isThreadResolved}
                    onResolve={handleResolveThread}
                    onSendTelemetry={sendUnifiedCommentActionTelemetry}
                    onUnresolve={handleUnresolveThread}
                    resolvedBy={thread.resolvedBy}
                    resolveButtonLabel={resolveButtonLabel}
                    showBatchToggle={showBatchToggle}
                    showResolutionReasons={showResolutionReasons}
                    unresolveButtonLabel={unresolveButtonLabel}
                  />
                ) : (
                  <div className="d-flex flex-items-center p-2 border-top flex-wrap">
                    {isThreadResolved ? (
                      <Button aria-label={unresolveButtonLabel} onClick={handleUnresolveThread} className="mr-2">
                        Unresolve comment
                      </Button>
                    ) : showResolutionReasons ? (
                      <div className="mr-2">
                        <ResolveThreadSplitButton
                          resolveButtonLabel={resolveButtonLabel}
                          onResolve={handleResolveThread}
                        />
                      </div>
                    ) : (
                      <Button aria-label={resolveButtonLabel} onClick={() => handleResolveThread()} className="mr-2">
                        Resolve comment
                      </Button>
                    )}
                    {isThreadResolved && thread.resolvedBy && (
                      <span className="fgColor-muted f6">
                        <b>{thread.resolvedBy}</b> marked this comment as resolved
                      </span>
                    )}
                  </div>
                ))}
              {dismissableAutomatedComment && (
                <InlineAutomatedReviewThreadActions
                  automatedComment={automatedComment}
                  thread={thread}
                  filePath={filePath}
                  lineMarkersKey={fileLevelCommentKey}
                  setErrorMessage={setErrorMessage}
                  setIsCollapsed={setIsCollapsed}
                />
              )}
            </>
          )}
        </Suspense>
      </div>
    </ErrorBoundary>
  )
}
